import { useChat } from "@ai-sdk/react";
import type { UIMessageChunk } from "ai";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { ClientError } from "../../../../api/src/shared/request";
import { displayToast } from "../../components/app/AppToast";
import { ConversationMenu } from "../../components/gpt/ConversationMenu";
import { GptConversationBreadcrumb } from "../../components/gpt/GptConversationBreadcrumb";
import { GptConversationHeader } from "../../components/gpt/GptConversationHeader";
import {
  GPTHistoryButton,
  type GptHistoryReveal,
} from "../../components/gpt/GPTHistoryPanel";
import { GptConversation } from "../../components/gpt/chat/GptConversation";
import type { GptAnswerForm } from "../../components/gpt/chat/GPTConversationTabs";
import { gptBackground } from "../../components/gpt/layout";
import type {
  GptSearchDoc,
  GptSearchPubmedDoc,
} from "../../components/gpt/chat/GptSearchTurn";
import { EvidenceCitationSheet } from "../../components/gpt/evidence/EvidenceCitationSheet";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import Loader from "../../components/Loader";
import { BackButton } from "../../components/ui/BackButton";
import { isDesktopShell } from "../../common/hooks/useIsDesktopView";
import { useLocationState } from "../../domains/app/hooks/useLocationState";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import type { AssistantOrigin } from "../../domains/assistant/assistantChatTransport";
import type {
  Redaction,
  RedactionNotice,
} from "../../domains/assistant/redaction";
import { useQuestionRedaction } from "../../domains/assistant/useQuestionRedaction";
import { askedFromSchema } from "../../domains/gpt/askedFrom";
import { useChatAttachments } from "../../domains/gpt/hooks/useChatAttachments";
import { useChatKeyboardScroll } from "../../domains/gpt/hooks/useChatKeyboardScroll";
import { useChatReferences } from "../../domains/gpt/hooks/useChatReferences";
import { useChatScrollAnchor } from "../../domains/gpt/hooks/useChatScrollAnchor";
import { useCopyAnswer } from "../../domains/gpt/hooks/useCopyAnswer";
import { useEnsureGptConversation } from "../../domains/gpt/hooks/useEnsureGptConversation";
import type {
  EvidenceGraphView,
  EvidenceSourceView,
} from "../../domains/gpt/hooks/useEvidenceGptChat";
import {
  toUIMessages,
  uiMessageQuestionCount,
  uiMessageText,
  type GeneralChatUIMessage,
} from "../../domains/gpt/hooks/useGptChat";
import {
  useDeleteConversation,
  useRenameConversation,
} from "../../domains/gpt/hooks/useManageConversation";
import {
  CONVERSATION_SHARE_FLAG,
  useShareConversation,
  useUnshareConversation,
} from "../../domains/gpt/hooks/useShareConversation";
import { legacyEvaluation } from "../../domains/gpt/stores/annotationSheetStore";
import { useKeyboardManaged } from "../../hooks/use-keyboard-managed";
import {
  apiClient,
  clearApiQuery,
  refetchApiQuery,
  useApiQuery,
  type Api,
} from "../../services/api";
import { gaEventGptInteraction } from "../../tools/analytics/domains/gpt.analytics";
import { z } from "zod";

// Same Back target as the other conversation pages: the landing, with the history revealed.
const HISTORY_ARRIVAL_REVEALING = { historyArrival: "revealing" } as const;

const locationStateSchema = z.object({ askedFrom: askedFromSchema });

// A legacy JuisciGPT conversation under the assistant engine. Its turns stay
// readable, and its next question continues it in an assistant conversation.
export function LegacyContinuationPage({
  id,
  roomId,
  historyReveal,
  conversation,
}: {
  id: string;
  roomId: string | null;
  historyReveal: GptHistoryReveal;
  conversation: Api["get"]["/webapp/private/conversations/:id"]["output"]["body"];
}): React.ReactElement {
  useKeyboardManaged();
  const { t } = useTranslation();
  const history = useHistory();
  const { gptNewChatPageLink, gptConversationPageLink, articleLink } =
    usePageLinks(roomId);
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null ? { param: { id: roomId } } : false
  );
  const isShareEnabled = useFeatureIsOn(CONVERSATION_SHARE_FLAG);
  const { scrollRef, isAtBottom, scrollToBottom, onScroll } =
    useChatScrollAnchor({ initialToBottom: true });
  const { paddingBottom: keyboardPaddingBottom } =
    useChatKeyboardScroll(scrollToBottom);
  const copyAnswer = useCopyAnswer();
  const shareConversation = useShareConversation();
  const unshareConversation = useUnshareConversation();
  const renameConversation = useRenameConversation();
  const deleteConversation = useDeleteConversation();
  // Its files and playlists stay listed; the continuation receives a copy of each.
  const ensured = useEnsureGptConversation({
    conversationId: id,
    engine: "legacy",
  });
  const attachments = useChatAttachments({ conversation: ensured });
  const references = useChatReferences({ conversation: ensured });
  // Minted once, so a retried continuation reuses the conversation it may have created.
  const [continuationId] = useState(() => crypto.randomUUID());
  // The route of each question by its message id, which a retry sends again.
  const [routes] = useState(
    () =>
      new Map<
        string,
        { origin: AssistantOrigin; notice: RedactionNotice | null }
      >()
  );
  const [openEvidenceSource, setOpenEvidenceSource] = useState<{
    source: EvidenceSourceView;
    citationNumber: number;
    quotes: readonly string[];
  } | null>(null);
  const initialMessages = toUIMessages(conversation.messages);
  const { askedFrom } = useLocationState(locationStateSchema);
  // A question creates the continuation, then goes to its page, which streams the answer.
  // A failed request keeps the question in the transcript with a retry.
  const chat = useChat<GeneralChatUIMessage>({
    id,
    messages: initialMessages,
    transport: {
      sendMessages: ({ messages, abortSignal }) => {
        const question = messages.at(-1);
        if (question === undefined || question.role !== "user")
          throw new Error("should not happen: a send without its question");
        const route = routes.get(question.id);
        if (route === undefined)
          throw new Error("should not happen: a question without its route");
        if (abortSignal === undefined)
          throw new Error("should not happen: a send without its stop");
        // A failed request rejects to `useChat`, which shows it on the turn.
        // A continuation that already exists answers with its own id.
        return apiClient
          .post("/assistant/legacy/:conversationId/continue", {
            param: { conversationId: id },
            json: { id: continuationId },
            // Stop frees the composer at once; the server may still create the continuation, which the next send finds.
            signal: abortSignal,
          })
          .then(({ body }) =>
            // The continuation's page seeds its chat from this read, the legacy turns included.
            refetchApiQuery("/assistant/conversations/:conversationId", {
              param: { conversationId: body.id },
            }).then(() => body.id)
          )
          .then((continuedAs) => {
            // A stopped question stays here; the continuation without it is harmless.
            abortSignal.throwIfAborted();
            // The next read of this id redirects to the continuation.
            clearApiQuery("/webapp/private/conversations/:id", {
              param: { id },
            });
            // No `?new`: the question continues a conversation, so its
            // analytics report "old" and rank it after the legacy turns.
            history.replace(gptConversationPageLink(continuedAs), {
              pending: {
                question: uiMessageText(question),
                origin: route.origin,
                // The question's id: a send repeated after a lost response is one run.
                requestId: question.id,
                notice: route.notice,
              },
              // The replace drops this page's state, so the Explore breadcrumb rides along.
              askedFrom,
            });
            // The continuation's page streams the answer.
            return new ReadableStream<UIMessageChunk>({
              start: (controller) => {
                controller.close();
              },
            });
          });
      },
      reconnectToStream: () => {
        throw new Error("should not happen: a legacy view resumes nothing");
      },
    },
    onError: (error) => {
      // 409: a legacy answer is still being written, or a file changed during the copy; Retry succeeds once it settles. Other failures show on the turn.
      if (error instanceof ClientError && error.responseStatus === 409)
        displayToast(
          t("This conversation is still working. Try again in a moment."),
          "error"
        );
    },
  });
  const questionRedaction = useQuestionRedaction<AssistantOrigin>({
    enabled: true,
    deliver: submit,
    failedTextPlace: {
      kind: "transcript",
      questionCount: chat.messages.reduce(
        (count, message) => count + uiMessageQuestionCount(message),
        0
      ),
    },
  });

  if (roomId !== null && roomQuery.state === "pending" && roomQuery.isFetching)
    return <Loader expanded />;

  const evidenceGraphByTurnIndex = new Map<number, EvidenceGraphView>(
    conversation.evidenceAnswers.map((turn) => [turn.turnIndex, turn.answer])
  );
  // A refinement chip would start a legacy Search turn, so the rows show without them.
  const searchByTurnIndex = new Map<
    number,
    {
      framing: string;
      docs: GptSearchDoc[];
      pubmedDocs: GptSearchPubmedDoc[];
      refinements: string[];
    }
  >(
    conversation.searchTurns.map((turn) => [
      turn.turnIndex,
      {
        framing: turn.framing,
        docs: turn.docs,
        pubmedDocs: turn.pubmedDocs,
        refinements: [],
      },
    ])
  );
  const questionCount = chat.messages.filter(
    (message) => message.role === "user"
  ).length;

  function submit(redaction: Redaction, origin: AssistantOrigin): void {
    if (redaction.text === "") return;
    // The UUID the continuation's page sends the question under.
    const requestId = crypto.randomUUID();
    routes.set(requestId, { origin, notice: redaction.notice });
    // A question that failed or stopped was never asked: the new one replaces it.
    chat.setMessages(initialMessages);
    void chat.sendMessage({
      id: requestId,
      parts: [{ type: "text", text: redaction.text }],
    });
  }

  function handleCopyAnswer(
    question: string,
    text: string,
    answerForm: GptAnswerForm
  ): void {
    copyAnswer(question, text, () => {
      // The copied answers are the legacy engine's.
      gaEventGptInteraction({
        engine: "legacy",
        isNewConversation: false,
        isSuggestedQuestion: false,
        questionCount,
        interaction: "copy_answer",
        answerForm,
      });
      displayToast(
        isDesktopShell() ? t("Answer copied") : t("Copied"),
        "success"
      );
    });
  }

  function comingSoon(): void {
    displayToast(t("Coming soon"), "info");
  }

  function handleBack(): void {
    history.replace(gptNewChatPageLink(), HISTORY_ARRIVAL_REVEALING);
  }

  async function handleDelete(): Promise<boolean> {
    const deleted = await deleteConversation(id);
    if (deleted === null) return false;
    handleBack();
    return true;
  }

  const shareThisConversation = isShareEnabled
    ? () => {
        void shareConversation(id, questionCount);
      }
    : null;

  const conversationMenu = (
    <ConversationMenu
      title={conversation.title}
      onShare={shareThisConversation}
      onUnshare={
        isShareEnabled && conversation.isShared
          ? () => {
              void unshareConversation(id);
            }
          : null
      }
      onDebug={null}
      onRename={(nextTitle) => renameConversation(id, nextTitle)}
      onDelete={handleDelete}
      className={null}
    />
  );

  const desktopLeading = isDesktopShell() ? (
    <GptConversationBreadcrumb
      askedFrom={askedFrom}
      newChatLink={gptNewChatPageLink()}
      onBackToGpt={handleBack}
      current={
        conversation.title === null ? t("New conversation") : conversation.title
      }
    />
  ) : (
    <>
      <BackButton
        onClick={handleBack}
        className="rounded-full bg-white/80 px-3 py-1.5 shadow-[0px_2px_8px_rgba(0,0,0,0.08)] backdrop-blur-md hover:bg-white"
      >
        {t("Back")}
      </BackButton>
      <GPTHistoryButton
        onClick={
          historyReveal.isOpen ? historyReveal.close : historyReveal.open
        }
        className="ml-1"
      />
    </>
  );

  return (
    <>
      <ScrollableLayout
        background={gptBackground()}
        headerClassName="bg-white/90 backdrop-blur-md"
        header={
          <GptConversationHeader
            title={conversation.title}
            onBack={handleBack}
            visualAbstracts={null}
            newConversationTo={gptNewChatPageLink()}
            actions={conversationMenu}
          />
        }
        scrollRef={scrollRef}
        onScroll={onScroll}
        style={{
          ...historyReveal.scrollLockStyle,
          paddingBottom: keyboardPaddingBottom,
        }}
        hideScrollbar
      >
        <GptConversation
          chat={chat}
          leading={desktopLeading}
          // The desktop top bar carries the panel toggle alone (Figma): the
          // sidebar's Recent and the history rename and delete.
          headerActions={isDesktopShell() ? null : conversationMenu}
          composerNotice={null}
          afterTurns={null}
          suggestion={null}
          pushOptIn={null}
          attachments={attachments}
          // The files belong to the legacy conversation, which takes no new turn.
          canModifyAttachments={false}
          references={references}
          study={null}
          canModifyReferences={false}
          cwyopPaper={null}
          showModePicker={false}
          evidenceGraphByTurnIndex={evidenceGraphByTurnIndex}
          evidenceCheckByRunId={new Map()}
          onOpenEvidenceSource={(source, citationNumber, quotes) => {
            gaEventGptInteraction({
              engine: "legacy",
              isNewConversation: false,
              isSuggestedQuestion: false,
              questionCount,
              interaction: "open_source",
              answerForm: "evidence_graph",
            });
            setOpenEvidenceSource({ source, citationNumber, quotes });
          }}
          pendingEvidence={null}
          searchByTurnIndex={searchByTurnIndex}
          pendingSearch={null}
          onOpenSearchArticle={(article) => {
            history.push(articleLink(article));
          }}
          onRefineSearch={() => undefined}
          onAskGeneral={null}
          onAnswerCard={null}
          answerMath={false}
          actions={{
            onCopy: handleCopyAnswer,
            onShare:
              shareThisConversation === null
                ? comingSoon
                : shareThisConversation,
            showShare: isShareEnabled,
            onRegenerate: null,
            // Sends the failed question again, under the same id.
            onRetry: () => {
              void chat.regenerate();
            },
            // The legacy turns keep their evaluation.
            ...legacyEvaluation(id, conversation.myAnnotations),
            comingSoon,
          }}
          followUps={null}
          disclaimer={t(
            "AI can make mistakes. Consider checking Source information."
          )}
          background={gptBackground()}
          roomId={roomId}
          assistantConversationId={null}
          viewStateKey={id}
          onOpenSources={(answerForm) => {
            gaEventGptInteraction({
              engine: "legacy",
              isNewConversation: false,
              isSuggestedQuestion: false,
              questionCount,
              interaction: "open_reference",
              answerForm,
            });
          }}
          onOpenSource={(answerForm) => {
            gaEventGptInteraction({
              engine: "legacy",
              isNewConversation: false,
              isSuggestedQuestion: false,
              questionCount,
              interaction: "open_source",
              answerForm,
            });
          }}
          isReconnecting={false}
          hasLostTurn={false}
          followKeyboard={!historyReveal.isOpen}
          composer={{
            kind: "assistant",
            onSubmit: (question) => {
              questionRedaction.sendRedacted(question, "typed");
            },
            // A stopped question was never asked, so it leaves the transcript.
            onStop: () => {
              void chat.stop().then(() => {
                chat.setMessages(initialMessages);
              });
            },
            placeholder: isDesktopShell()
              ? t("Describe a case, a decision, or a clinical doubt…")
              : t("Keep asking..."),
            // The continuation's page sends the question and shows its Notice.
            banner: questionRedaction.showInputRule
              ? {
                  kind: "inputRule",
                  onDismiss: questionRedaction.dismissInputRule,
                }
              : null,
            unsent: questionRedaction.unsent,
            overlapsMatch: questionRedaction.overlapsMatch,
          }}
          scrollRef={scrollRef}
          isAtBottom={isAtBottom}
          onScrollToBottom={scrollToBottom}
          revealAssistantId={null}
        />
      </ScrollableLayout>
      <EvidenceCitationSheet
        source={openEvidenceSource === null ? null : openEvidenceSource.source}
        citationNumber={
          openEvidenceSource === null ? null : openEvidenceSource.citationNumber
        }
        quotes={openEvidenceSource === null ? [] : openEvidenceSource.quotes}
        roomId={roomId}
        isOpen={openEvidenceSource !== null}
        onClose={() => {
          setOpenEvidenceSource(null);
        }}
      />
    </>
  );
}
